import type { DateString } from '../common';

/**
 * Security events: authentication outcomes and authorization refusals recorded by the
 * backend (erp-backend #113). Read-only, platform admin only, served by
 * GET /admin/security-events.
 */
export const SECURITY_EVENT = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGOUT: 'LOGOUT',
  LOGOUT_ALL: 'LOGOUT_ALL',
  ADMIN_FORCE_LOGOUT: 'ADMIN_FORCE_LOGOUT',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  PASSWORD_RESET_COMPLETED: 'PASSWORD_RESET_COMPLETED',
  PASSWORD_RESET_TOKEN_REJECTED: 'PASSWORD_RESET_TOKEN_REJECTED',
  REGISTRATION: 'REGISTRATION',
  TOKEN_REFRESH_FAILED: 'TOKEN_REFRESH_FAILED',
  ACCESS_DENIED_ROLE: 'ACCESS_DENIED_ROLE',
  ACCESS_DENIED_TENANT: 'ACCESS_DENIED_TENANT',
  ACCESS_DENIED_SUPER_ADMIN: 'ACCESS_DENIED_SUPER_ADMIN',
  API_KEY_REJECTED: 'API_KEY_REJECTED',
} as const;

export type SecurityEventType = (typeof SECURITY_EVENT)[keyof typeof SECURITY_EVENT];

export type SecurityEvent = {
  id: string;
  // Naive Athens datetime: no Z and no offset.
  occurredAt: DateString;
  event: SecurityEventType;
  userId: string | null;
  // The acting or the attempted email.
  userEmail: string | null;
  rentalId: string | null;
  // Set on ADMIN_FORCE_LOGOUT only: the user whose sessions were ended.
  targetUserId: string | null;
  ip: string;
  userAgent: string | null;
  // Links the row to its New Relic trace (kept for eight days).
  requestId: string;
  // Set on refusals only.
  method: string | null;
  path: string | null;
  // A short code, or the required roles joined by a comma for ACCESS_DENIED_ROLE. Old rows
  // can carry a code the frontend does not know, so it stays a plain string.
  reason: string | null;
};

const SECURITY_EVENT_LABELS: Record<SecurityEventType, string> = {
  [SECURITY_EVENT.LOGIN_SUCCESS]: 'Login succeeded',
  [SECURITY_EVENT.LOGIN_FAILED]: 'Login failed',
  [SECURITY_EVENT.LOGOUT]: 'Logout',
  [SECURITY_EVENT.LOGOUT_ALL]: 'Logout from all sessions',
  [SECURITY_EVENT.ADMIN_FORCE_LOGOUT]: 'Forced logout by admin',
  [SECURITY_EVENT.PASSWORD_CHANGED]: 'Password changed',
  [SECURITY_EVENT.PASSWORD_RESET_COMPLETED]: 'Password reset completed',
  [SECURITY_EVENT.PASSWORD_RESET_TOKEN_REJECTED]: 'Password reset link rejected',
  [SECURITY_EVENT.REGISTRATION]: 'Registration',
  [SECURITY_EVENT.TOKEN_REFRESH_FAILED]: 'Token refresh failed',
  [SECURITY_EVENT.ACCESS_DENIED_ROLE]: 'Access denied: role',
  [SECURITY_EVENT.ACCESS_DENIED_TENANT]: 'Access denied: rental',
  [SECURITY_EVENT.ACCESS_DENIED_SUPER_ADMIN]: 'Access denied: platform route',
  [SECURITY_EVENT.API_KEY_REJECTED]: 'API key rejected',
};

/**
 * Human-readable label for a security event. An event the frontend does not know yet
 * shows its raw value rather than a blank cell.
 */
export const getSecurityEventLabel = (event: string): string =>
  SECURITY_EVENT_LABELS[event as SecurityEventType] ?? event;

export const SECURITY_EVENT_OPTIONS: Array<{ value: SecurityEventType; label: string }> =
  Object.values(SECURITY_EVENT).map((value) => ({
    value,
    label: SECURITY_EVENT_LABELS[value],
  }));

const SECURITY_EVENT_SUCCESS = new Set<string>([
  SECURITY_EVENT.LOGIN_SUCCESS,
  SECURITY_EVENT.PASSWORD_CHANGED,
  SECURITY_EVENT.PASSWORD_RESET_COMPLETED,
  SECURITY_EVENT.REGISTRATION,
]);

const SECURITY_EVENT_DANGER = new Set<string>([
  SECURITY_EVENT.LOGIN_FAILED,
  SECURITY_EVENT.PASSWORD_RESET_TOKEN_REJECTED,
  SECURITY_EVENT.TOKEN_REFRESH_FAILED,
  SECURITY_EVENT.ACCESS_DENIED_ROLE,
  SECURITY_EVENT.ACCESS_DENIED_TENANT,
  SECURITY_EVENT.ACCESS_DENIED_SUPER_ADMIN,
  SECURITY_EVENT.API_KEY_REJECTED,
]);

export type SecurityEventTone = 'success' | 'neutral' | 'danger';

/** Failures and refusals are danger; logouts and unknown events are neutral. */
export const getSecurityEventTone = (event: string): SecurityEventTone => {
  if (SECURITY_EVENT_SUCCESS.has(event)) return 'success';
  if (SECURITY_EVENT_DANGER.has(event)) return 'danger';
  return 'neutral';
};
